// src/pages/PatientPrescriptions.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
} from "firebase/firestore";
import { ArrowLeft, Pill, Clock, Info } from "lucide-react";

import { auth, db } from "../firebase";

/** 🔧 CHANGE THIS IF YOUR COLLECTION NAME IS DIFFERENT */
const MEDICAL_RECORDS_COLLECTION = "medicalRecords";

/* ---------- helpers ---------- */
const toDate = (v) => {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (typeof v?.toDate === "function") return v.toDate();
  const d = new Date(v);
  return isNaN(d) ? null : d;
};

const formatDate = (d) =>
  d?.toLocaleDateString("sq-AL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }) || "";

const formatTime = (t) => t || "";

/* ---------- main page ---------- */
export default function PatientPrescriptions() {
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load medicalRecords for this patient
  useEffect(() => {
    // if user not loaded / not logged in, clear and stop
    if (!user) {
      setRecords([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const ref = collection(db, MEDICAL_RECORDS_COLLECTION);
    const q = query(
      ref,
      where("patientId", "==", user.uid),
      orderBy("createdAt", "desc")
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setRecords(list);
        setLoading(false);
      },
      (err) => {
        console.error("Error loading medical records:", err);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [user]);

  // Flatten prescriptions from each record
  const prescriptions = useMemo(() => {
    const out = [];

    records.forEach((rec) => {
      const recPresc = rec.prescriptions || [];
      if (!Array.isArray(recPresc) || recPresc.length === 0) return;

      recPresc.forEach((p) => {
        out.push({
          ...p,
          _recordId: rec.id,
          _appointmentDate: rec.appointmentDate || "",
          _appointmentTime: rec.appointmentTime || "",
          _doctorName: rec.doctorInfo?.name || "",
          _specialty: rec.doctorInfo?.specialty || "",
          _createdAt: rec.createdAt,
        });
      });
    });

    // sort newest first by updatedAt or createdAt
    out.sort((a, b) => {
      const ta = toDate(a.updatedAt || a._createdAt)?.getTime() || 0;
      const tb = toDate(b.updatedAt || b._createdAt)?.getTime() || 0;
      return tb - ta;
    });

    return out;
  }, [records]);

  // Group prescriptions by visit (recordId)
  const groupedByVisit = useMemo(() => {
    const map = new Map();

    prescriptions.forEach((p) => {
      const key = p._recordId || `${p._appointmentDate}-${p._doctorName}`;
      if (!map.has(key)) {
        map.set(key, {
          recordId: key,
          doctorName: p._doctorName || "",
          specialty: p._specialty || "",
          appointmentDate: p._appointmentDate || "",
          appointmentTime: p._appointmentTime || "",
          updatedAt: p.updatedAt || p._createdAt || null,
          items: [],
        });
      }
      map.get(key).items.push(p);
    });

    // sort visits by newest updated/created
    const visits = Array.from(map.values());
    visits.sort((a, b) => {
      const ta = toDate(a.updatedAt)?.getTime() || 0;
      const tb = toDate(b.updatedAt)?.getTime() || 0;
      return tb - ta;
    });

    return visits;
  }, [prescriptions]);

  if (!user) {
    return (
      <div
        style={{
          padding: 16,
          maxWidth: 480,
          margin: "0 auto",
          fontFamily:
            '-apple-system,BlinkMacSystemFont,"SF Pro Text","SF Pro Display",system-ui,"Segoe UI",Roboto,Helvetica,Arial,sans-serif',
          fontSize: 14,
          color: "#4B5563",
        }}
      >
        Duhet të hysh në llogarinë tënde për të parë recetat mjekësore.
      </div>
    );
  }

  return (
    <div
      style={{
        padding: 16,
        maxWidth: 480,
        margin: "0 auto",
        fontFamily:
          '-apple-system,BlinkMacSystemFont,"SF Pro Text","SF Pro Display",system-ui,"Segoe UI",Roboto,Helvetica,Arial,sans-serif',
      }}
    >
      {/* TOP HEADER (back + title) */}
      <header
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          marginBottom: 12,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          {/* back button */}
          <button
            type="button"
            onClick={() => navigate(-1)}
            style={{
              width: 32,
              height: 32,
              borderRadius: 999,
              border: "1px solid #E5E7EB",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#FFFFFF",
              cursor: "pointer",
            }}
          >
            <ArrowLeft style={{ width: 18, height: 18, color: "#4B5563" }} />
          </button>

          {/* title + subtitle */}
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: "#0F172A",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <Pill style={{ width: 18, height: 18, color: "#0F766E" }} />
              Recetat mjekësore
            </span>
            <span
              style={{
                fontSize: 12,
                color: "#6B7280",
              }}
            >
              Shiko të gjitha ilaçet që janë përshkruar gjatë vizitave në
              MikDok, me dozën, frekuencën dhe kohëzgjatjen.
            </span>
          </div>

          <div style={{ flex: 1 }} />
        </div>
      </header>

      {/* CARD – prescriptions grouped by visits */}
      <div
        style={{
          borderRadius: 18,
          border: "1px solid #E5E7EB",
          background: "#FFFFFF",
          boxShadow: "0 10px 30px -18px rgba(15,23,42,0.25)",
          overflow: "hidden",
        }}
      >
        {/* Header strip */}
        <div
          style={{
            padding: "10px 14px",
            borderBottom: "1px solid #E5E7EB",
            background:
              "linear-gradient(135deg, rgba(239,246,255,0.95), rgba(236,252,245,0.95))",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "#0F172A",
            }}
          >
            Recetat e tua
          </span>
          <span
            style={{
              fontSize: 11,
              padding: "3px 8px",
              borderRadius: 999,
              border: "1px solid rgba(148,163,184,0.4)",
              background: "rgba(248,250,252,0.9)",
              color: "#475569",
              whiteSpace: "nowrap",
            }}
          >
            {prescriptions.length === 0
              ? "Asnjë recetë"
              : prescriptions.length === 1
              ? "1 recetë"
              : `${prescriptions.length} receta`}
          </span>
        </div>

        {/* Body (scrollable) */}
        <div
          style={{
            maxHeight: "60vh",
            overflowY: "auto",
            padding: 10,
          }}
        >
          {loading ? (
            <div
              style={{
                padding: 20,
                fontSize: 13,
                color: "#6B7280",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <Clock style={{ width: 16, height: 16, color: "#9CA3AF" }} />
              Po ngarkojmë recetat e tua…
            </div>
          ) : prescriptions.length === 0 ? (
            <div
              style={{
                padding: 24,
                textAlign: "center",
                fontSize: 13,
                color: "#6B7280",
              }}
            >
              Nuk ke asnjë recetë të regjistruar në MikDok.
              <div
                style={{
                  marginTop: 6,
                  fontSize: 12,
                  color: "#9CA3AF",
                  display: "flex",
                  justifyContent: "center",
                  gap: 4,
                }}
              >
                <Info style={{ width: 14, height: 14 }} />
                Recetat nga vizitat te mjekët në MikDok do të shfaqen automatikisht
                këtu.
              </div>
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gap: 12,
              }}
            >
              {groupedByVisit.map((visit) => {
                const visitDate = visit.appointmentDate
                  ? formatDate(new Date(visit.appointmentDate))
                  : "";
                const visitTime = formatTime(visit.appointmentTime);
                const visitUpdated = toDate(visit.updatedAt);
                const visitUpdatedLabel = visitUpdated
                  ? formatDate(visitUpdated)
                  : null;

                return (
                  <div
                    key={visit.recordId}
                    style={{
                      borderRadius: 14,
                      border: "1px solid #E5E7EB",
                      padding: 8,
                      background: "#F9FAFB",
                    }}
                  >
                    {/* visit header */}
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        marginBottom: 6,
                        padding: "2px 2px 4px",
                        borderBottom: "1px dashed #E5E7EB",
                      }}
                    >
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: "#111827",
                        }}
                      >
                        Vizitë te{" "}
                        {visit.doctorName
                          ? `Dr. ${visit.doctorName}`
                          : "mjeku"}
                        {visit.specialty && ` · ${visit.specialty}`}
                      </span>
                      {(visitDate || visitTime || visitUpdatedLabel) && (
                        <span
                          style={{
                            fontSize: 11,
                            color: "#6B7280",
                          }}
                        >
                          {visitDate}
                          {visitTime && `, ora ${visitTime}`}
                          {visitUpdatedLabel &&
                            ` · Përditësuar: ${visitUpdatedLabel}`}
                        </span>
                      )}
                      <span
                        style={{
                          fontSize: 11,
                          color: "#0F766E",
                        }}
                      >
                        {visit.items.length} ilaç
                        {visit.items.length === 1 ? "" : "e"} në këtë recetë
                      </span>
                    </div>

                    {/* prescriptions in this visit */}
                    <div
                      style={{
                        display: "grid",
                        gap: 6,
                      }}
                    >
                      {visit.items.map((p) => (
                        <PrescriptionCard
                          key={p.id || `${visit.recordId}-${p.medication}`}
                          prescription={p}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* 🔻 NON-SCROLLABLE PROMO SECTION (always visible at the bottom) */}
      <div
        style={{
          marginTop: 16,
          borderRadius: 16,
          padding: "12px 14px",
          background:
            "linear-gradient(135deg, rgba(59,130,246,0.08), rgba(45,212,191,0.08))",
          border: "1px solid rgba(148,163,184,0.4)",
          display: "flex",
          flexDirection: "column",
          gap: 6,
        }}
      >
        <span
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: "#0F172A",
          }}
        >
          Ruaj shëndetin tënd me MikDok 💙
        </span>
        <span
          style={{
            fontSize: 12,
            color: "#4B5563",
          }}
        >
          Nëse po merr ilaçe për një kohë të gjatë, planifiko një kontroll
          të radhës te mjeku për të rishikuar terapinë dhe analizat.
        </span>

        <button
          type="button"
          onClick={() => navigate("/patient/appointments")}
          style={{
            marginTop: 6,
            alignSelf: "flex-start",
            padding: "6px 12px",
            borderRadius: 999,
            border: "none",
            fontSize: 12,
            fontWeight: 500,
            background: "#2563EB",
            color: "#FFFFFF",
            cursor: "pointer",
          }}
        >
          Rezervo një kontroll
        </button>
      </div>
    </div>
  );
}

/* ---------- Single prescription card (from medicalRecord.prescriptions[]) ---------- */
function PrescriptionCard({ prescription }) {
  const updated = toDate(prescription.updatedAt || prescription._createdAt);
  const updatedLabel = updated ? `${formatDate(updated)}` : null;

  const appointmentDate = prescription._appointmentDate
    ? formatDate(new Date(prescription._appointmentDate))
    : "";
  const appointmentTime = formatTime(prescription._appointmentTime);

  return (
    <div
      style={{
        borderRadius: 12,
        border: "1px solid #E5E7EB",
        padding: "10px 10px",
        display: "flex",
        flexDirection: "column",
        gap: 4,
        background: "#FFFFFF",
      }}
    >
      {/* Top row: medication name + duration */}
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: "#111827",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {prescription.medication || "Ilaç pa emër"}
          </div>
          {prescription.dosage && (
            <div
              style={{
                fontSize: 12,
                color: "#4B5563",
                marginTop: 2,
              }}
            >
              Doza: {prescription.dosage}
            </div>
          )}
        </div>

        {prescription.duration && (
          <div
            style={{
              fontSize: 11,
              padding: "3px 8px",
              borderRadius: 999,
              background: "rgba(14,165,233,0.06)",
              color: "#0369a1",
              flexShrink: 0,
              whiteSpace: "nowrap",
            }}
          >
            {prescription.duration}
          </div>
        )}
      </div>

      {/* Frequency */}
      {prescription.frequency && (
        <div
          style={{
            marginTop: 4,
            fontSize: 12,
            color: "#4B5563",
          }}
        >
          Frekuenca:{" "}
          <span style={{ fontWeight: 500 }}>{prescription.frequency}</span>
        </div>
      )}

      {/* Instructions */}
      {prescription.instructions && (
        <div
          style={{
            marginTop: 4,
            fontSize: 11,
            color: "#4B5563",
            background: "#F9FAFB",
            borderRadius: 8,
            padding: "6px 8px",
            lineHeight: 1.4,
          }}
        >
          {prescription.instructions}
        </div>
      )}

      {/* Symptoms */}
      {prescription.symptoms && (
        <div
          style={{
            marginTop: 4,
            fontSize: 11,
            color: "#6B7280",
          }}
        >
          <span style={{ fontWeight: 500 }}>Simptomat: </span>
          {prescription.symptoms}
        </div>
      )}

      {/* Doctor + appointment info (optional mini footer) */}
      {(appointmentDate || appointmentTime || updatedLabel) && (
        <div
          style={{
            marginTop: 6,
            fontSize: 10,
            color: "#9CA3AF",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {appointmentDate || appointmentTime ? (
            <span>
              Vizita: {appointmentDate}
              {appointmentTime && `, ora ${appointmentTime}`}
            </span>
          ) : null}
          {updatedLabel && <span>Përditësuar: {updatedLabel}</span>}
        </div>
      )}
    </div>
  );
}
